import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError, eur, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Plus, Trash2, Receipt, FileDown, Mail, CircleDollarSign, BellRing,
} from "lucide-react";

// iscritto e confermato sono automatici (assegnati dal sistema): l'operatore può solo far avanzare
// manualmente lo stato lungo questi passaggi.
const ENROLLMENT_TRANSITIONS = {
  iscritto: ["annullato"],
  confermato: ["presente", "assente", "annullato"],
  presente: ["annullato"],
  assente: ["annullato"],
  annullato: [],
};
const COST_CATEGORIES = [
  "Docente", "Aula/Location", "Materiale didattico", "Attestati",
  "Vitto/Catering", "Trasferte", "Attrezzatura/DPI dimostrativi", "Altro",
];

export default function CorsoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [costOpen, setCostOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/courses/${id}`);
    setCourse(data);
  }, [id]);

  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers(data));
  }, [load]);

  const costFields = [
    {
      name: "category", label: "Tipo di costo", type: "select", full: true,
      options: COST_CATEGORIES.map((c) => ({ value: c, label: c })),
    },
    { name: "description", label: "Descrizione", required: true, full: true },
    { name: "amount", label: "Importo €", type: "number", required: true },
  ];

  const enrollFields = [
    {
      name: "customer_id", label: "Cliente da fatturare", type: "select", required: true, full: true,
      options: customers.map((c) => ({ value: c.id, label: c.name })),
    },
    { name: "attendee_name", label: "Nome partecipante", required: true, full: true },
    { name: "attendee_email", label: "Email partecipante", type: "email" },
    { name: "attendee_phone", label: "Telefono partecipante" },
    { name: "attendee_fiscal_code", label: "Codice fiscale (per attestato)" },
    { name: "price", label: "Quota €", type: "number" },
  ];

  const addCost = async (values) => {
    try {
      await api.post(`/courses/${id}/costs`, values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const addEnrollment = async (values) => {
    try {
      const { data } = await api.post(`/courses/${id}/enrollments`, values);
      setMsg(data.email_sent ? `Iscrizione registrata, email di conferma inviata a ${values.attendee_email}.`
                              : "Iscrizione registrata.");
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const setEnrollStatus = async (e, status) => {
    try {
      await api.put(`/enrollments/${e.id}`, { status });
      await load();
    } catch (err) {
      setMsg(apiError(err.response?.data?.detail));
    }
  };

  const solicit = async (e) => {
    try {
      const { data } = await api.post(`/enrollments/${e.id}/solicit`);
      setMsg(`Sollecito inviato a ${data.sent_to}.`);
    } catch (err) {
      setMsg(apiError(err.response?.data?.detail));
    }
  };

  const doInvoice = async (e) => {
    try {
      await api.post(`/enrollments/${e.id}/invoice`);
      setMsg(`Iscrizione ${e.code} fatturata.`);
      await load();
    } catch (err) {
      setMsg(apiError(err.response?.data?.detail));
    }
  };

  const downloadPdf = async (e) => {
    try {
      const { data } = await api.get(`/enrollments/${e.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${e.invoice_code || "proforma"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMsg("Download della proforma non riuscito.");
    }
  };

  const sendInvoice = async (e) => {
    try {
      const { data } = await api.post(`/enrollments/${e.id}/send-invoice`);
      setMsg(`Proforma ${e.invoice_code} inviata a ${data.sent_to}.`);
    } catch (err) {
      setMsg(apiError(err.response?.data?.detail));
    }
  };

  const downloadCreditNote = async (e) => {
    try {
      const { data } = await api.get(`/enrollments/${e.id}/credit-note.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${e.credit_note_code || "nota-credito"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMsg("Download della nota di credito non riuscito.");
    }
  };

  const sendCreditNote = async (e) => {
    try {
      const { data } = await api.post(`/enrollments/${e.id}/send-credit-note`);
      setMsg(`Nota di credito ${e.credit_note_code} inviata a ${data.sent_to}.`);
    } catch (err) {
      setMsg(apiError(err.response?.data?.detail));
    }
  };

  if (!course) return <p className="overline">Caricamento…</p>;
  const b = course.budget || {};
  const closed = course.status === "completato" || course.status === "annullato";

  return (
    <div data-testid="corso-detail-page">
      <PageHeader title={course.title} subtitle={`${course.code} · ${course.status}`}>
        {!closed && (
          <Button className="h-11 rounded-sm" onClick={() => setEnrollOpen(true)} data-testid="new-enrollment-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuova iscrizione
          </Button>
        )}
      </PageHeader>

      {msg && <p className="mb-4 text-sm font-medium text-primary" data-testid="corso-message">{msg}</p>}

      <div className={`grid sm:grid-cols-2 gap-4 mb-8 ${user?.role === "azienda" ? "lg:grid-cols-5" : ""}`}>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Data e sede</p>
          <p className="text-sm mt-2">{new Date(course.start_at).toLocaleString("it-IT")}</p>
          <p className="text-sm text-muted-foreground">{course.location || "—"}</p>
        </Card>
        {user?.role === "azienda" && (
          <>
            <Card className="p-5 rounded-sm shadow-none">
              <p className="overline">Previsto</p>
              <p className="font-display text-2xl font-black tracking-tighter mt-2">{eur(b.expected)}</p>
            </Card>
            <Card className="p-5 rounded-sm shadow-none">
              <p className="overline">Incassato</p>
              <p className="font-display text-2xl font-black tracking-tighter mt-2">{eur(b.collected)}</p>
            </Card>
            <Card className="p-5 rounded-sm shadow-none">
              <p className="overline">Costi</p>
              <p className="font-display text-2xl font-black tracking-tighter mt-2">{eur(b.costs)}</p>
            </Card>
            <Card className="p-5 rounded-sm shadow-none">
              <p className="overline">Margine</p>
              <p className={`font-display text-2xl font-black tracking-tighter mt-2 ${(b.margin || 0) < 0 ? "text-destructive" : ""}`}>
                {eur(b.margin)}
              </p>
            </Card>
          </>
        )}
      </div>

      <div className={`grid gap-6 ${user?.role === "azienda" ? "lg:grid-cols-3" : ""}`}>
        <div className={user?.role === "azienda" ? "lg:col-span-2" : ""}>
          <h3 className="font-display text-xl font-bold tracking-tight mb-3">Iscritti</h3>
          <ListCard testid="iscritti-list">
            {(course.enrollments || []).map((e) => (
              <div key={e.id} className="px-4 sm:px-6 py-4 flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{e.code}</span>
                  <p className="font-semibold">{e.attendee_name}</p>
                  <Badge variant="outline" className="rounded-sm capitalize">{e.status}</Badge>
                  {e.invoice_status && (
                    <Badge variant={e.invoice_status === "incassato" ? "secondary" : "default"}
                           className="rounded-sm capitalize">
                      {e.invoice_status.replace("_", " ")}
                    </Badge>
                  )}
                  {e.credit_note_code && (
                    <Badge variant="destructive" className="rounded-sm">NC {e.credit_note_code}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Fattura a: {e.customer_name} · {eur(e.price)}
                  {e.attendee_fiscal_code ? ` · CF ${e.attendee_fiscal_code}` : ""}
                </p>
                <div className="flex flex-wrap gap-2">
                  {user?.role === "azienda" && ENROLLMENT_TRANSITIONS[e.status]?.length > 0 && (
                    <select
                      className="h-9 rounded-sm border border-border bg-background px-2 text-sm"
                      value={e.status}
                      onChange={(ev) => setEnrollStatus(e, ev.target.value)}
                      data-testid={`enrollment-status-${e.id}`}
                    >
                      <option value={e.status} disabled>{e.status}</option>
                      {ENROLLMENT_TRANSITIONS[e.status].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                  {e.status === "iscritto" && (
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => solicit(e)}
                            data-testid={`solicit-enrollment-${e.id}`}>
                      <BellRing className="h-3.5 w-3.5 mr-2" /> Sollecito
                    </Button>
                  )}
                  {user?.role === "azienda" && e.invoice_status === "da_fatturare" && (
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => doInvoice(e)}
                            data-testid={`invoice-enrollment-${e.id}`}>
                      <Receipt className="h-3.5 w-3.5 mr-2" /> Fattura
                    </Button>
                  )}
                  {user?.role === "azienda" && e.invoice_status && e.invoice_status !== "da_fatturare" && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => downloadPdf(e)}
                              data-testid={`enrollment-pdf-${e.id}`}>
                        <FileDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => sendInvoice(e)}
                              data-testid={`send-enrollment-invoice-${e.id}`}>
                        <Mail className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                  {user?.role === "azienda" && e.credit_note_code && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                              onClick={() => downloadCreditNote(e)} data-testid={`credit-note-pdf-${e.id}`}>
                        <FileDown className="h-3.5 w-3.5 mr-2" /> NC
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                              onClick={() => sendCreditNote(e)} data-testid={`send-credit-note-${e.id}`}>
                        <Mail className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                  {user?.role === "azienda" && (
                    <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                            onClick={async () => { await api.delete(`/enrollments/${e.id}`); load(); }}
                            data-testid={`delete-enrollment-${e.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {(course.enrollments || []).length === 0 && <Empty text="Nessun iscritto." />}
          </ListCard>
        </div>

        {user?.role === "azienda" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl font-bold tracking-tight">Costi</h3>
              <Button size="sm" className="rounded-sm" onClick={() => setCostOpen(true)} data-testid="add-cost-btn">
                <Plus className="h-3.5 w-3.5 mr-2" /> Aggiungi
              </Button>
            </div>
            <ListCard testid="costi-list">
              {(course.costs || []).map((c) => (
                <div key={c.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{c.description}</p>
                      {c.category && (
                        <Badge variant="outline" className="rounded-sm">{c.category}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{itDate(c.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{eur(c.amount)}</span>
                    <Button variant="ghost" size="icon" className="text-destructive"
                            onClick={async () => { await api.delete(`/course-costs/${c.id}`); load(); }}
                            data-testid={`delete-cost-${c.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {(course.costs || []).length === 0 && <Empty text="Nessun costo registrato." />}
            </ListCard>
          </div>
        )}
      </div>

      <FormDialog open={costOpen} onOpenChange={setCostOpen} title="Aggiungi costo" fields={costFields}
                  initial={{}} onSubmit={addCost} testid="cost-dialog" />
      <FormDialog open={enrollOpen} onOpenChange={setEnrollOpen} title="Nuova iscrizione" fields={enrollFields}
                  initial={{ price: course.price }} onSubmit={addEnrollment} testid="enrollment-dialog" />
    </div>
  );
}
